import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SettingsService} from "../commons/settings/settings.service";
import {Skeleton} from "primeng/skeleton";
import {Card} from "primeng/card";
import {NgIf} from "@angular/common";
import {DropdownModule} from "primeng/dropdown";
import {Calendar} from "primeng/calendar";
import {InputNumber} from "primeng/inputnumber";
import {InputText} from "primeng/inputtext";
import {Divider} from "primeng/divider";
import {ButtonDirective} from "primeng/button";
import {forkJoin} from "rxjs";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {LocaleService} from "../../core/i18n/locale.service";

@Component({
  selector: 'app-home', standalone: true,
  imports: [
    Skeleton,
    Card,
    NgIf,
    ReactiveFormsModule,
    DropdownModule,
    Calendar,
    InputNumber,
    InputText,
    Divider,
    ButtonDirective,
    TranslatePipe
  ],
  templateUrl: './home.component.html'
})
export class HomeComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  loading = false;
  saving = false;

  form!: FormGroup;

  // track current values and ids
  original: Record<string, string> = {};
  byKey: Record<string, { id: string; value: string }> = {};

  statusOptions: { label: string; value: string }[] = [];

  constructor(private fb: FormBuilder, private settingsSvc: SettingsService) {}

  ngOnInit(): void {
    this.localeService.current$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.statusOptions = [
        { label: this.translate.instant('home.statusRunning'), value: 'running' },
        { label: this.translate.instant('home.statusUnderMaintenance'), value: 'underMaintenance' },
        { label: this.translate.instant('home.statusWaitingForLaunch'), value: 'waitingForLaunch' },
      ];
    });
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.settingsSvc.getAll().subscribe({
      next: (arr: any[]) => {
        const map: Record<string, string> = {};
        this.byKey = {};
        for (const s of arr ?? []) {
          const key = s?.key ?? s?.name;
          if (!key) continue;
          const val = String(s?.value ?? '');
          this.byKey[key] = { id: String(s.id), value: val };
          map[key] = val;
        }
        this.original = map;

        this.form = this.fb.group({
          // General
          application_status: [map['application_status'] ?? 'running'],
          maintenance_start_date: [this.toDate(map['maintenance_start_date'])],
          maintenance_minimum_days: [this.toNum(map['maintenance_minimum_days'])],
          maintenance_maximum_days: [this.toNum(map['maintenance_maximum_days'])],
          release_date: [this.toDate(map['release_date'])],

          // App Versions
          web_min_version: [map['web_min_version'] ?? ''],
          web_latest_version: [map['web_latest_version'] ?? ''],
          android_min_version: [map['android_min_version'] ?? ''],
          android_latest_version: [map['android_latest_version'] ?? ''],
          ios_min_version: [map['ios_min_version'] ?? ''],
          ios_latest_version: [map['ios_latest_version'] ?? ''],
        });

        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  save(): void {
    if (!this.form) return;

    // normalize values to strings for API
    const raw = { ...this.form.getRawValue() };
    raw.maintenance_start_date = this.fmt(raw.maintenance_start_date);
    raw.release_date = this.fmt(raw.release_date);
    raw.maintenance_minimum_days = String(raw.maintenance_minimum_days ?? '');
    raw.maintenance_maximum_days = String(raw.maintenance_maximum_days ?? '');

    const updates = Object.keys(raw)
      .filter((k) => !this.form.get(k)?.disabled)
      .map((k) => ({ key: k, newValue: String(raw[k] ?? '') }))
      .filter((u) => this.byKey[u.key]?.id && this.original[u.key] !== u.newValue);

    if (!updates.length) return;

    this.saving = true;
    forkJoin(updates.map((u) => this.settingsSvc.updateValue(this.byKey[u.key].id, u.newValue))).subscribe({
      next: () => {
        for (const u of updates) {
          this.original[u.key] = u.newValue;
          this.byKey[u.key].value = u.newValue;
        }
        this.form.markAsPristine();
        this.saving = false;
      },
      error: () => {
        this.saving = false;
      },
    });
  }

  // Helpers
  private toDate(s?: string) {
    if (!s) return null;
    const d = new Date(String(s).replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
  }

  private toNum(s?: string) {
    const n = Number(s);
    return isNaN(n) ? null : n;
  }

  private fmt(d: any) {
    if (!(d instanceof Date)) return d ?? '';
    const pad = (x: number) => (x < 10 ? '0' + x : '' + x);
    return (
      d.getFullYear() +
      '-' +
      pad(d.getMonth() + 1) +
      '-' +
      pad(d.getDate()) +
      ' ' +
      pad(d.getHours()) +
      ':' +
      pad(d.getMinutes()) +
      ':' +
      pad(d.getSeconds())
    );
  }
}
